/**
 * Site settings context — public, site-wide settings that shape the whole
 * app's chrome (FEA-30, docs/FRONTEND-PLAN.md §4.3): the announcement
 * banner, footer support email, feature-toggle nav/route gating, and the
 * Register page's "registration closed" notice.
 *
 * Fetches `GET /site-settings/public` with `{auth: false}` — this must work
 * for Login/Register too, which are rendered OUTSIDE the authenticated tree,
 * so this provider is mounted above the router's auth split in App.jsx, not
 * inside ProtectedRoute like NotificationsProvider.
 *
 * Refreshed on mount and whenever the tab becomes visible again (no interval
 * poll — settings change far less often than notifications). A failed fetch
 * is silent and keeps the last known value: the object below is also the
 * default while the very first fetch is in flight, deliberately "everything
 * enabled, no banner, registration open" so the app never flash-hides nav
 * items or the register form while loading.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import * as adminApi from '../api/admin';
import { getActiveOrgId } from './orgState';

export const DEFAULT_SITE_SETTINGS = Object.freeze({
  announcement: Object.freeze({ enabled: false, message: '', tone: 'info' }),
  supportEmail: null,
  registrationOpen: true,
  features: Object.freeze({ forum: true, events: true, calendar: true, tasks: true, groups: true, videos: true, inbox: true })
});

const SiteSettingsContext = createContext(null);

export function SiteSettingsProvider({ children }) {
  const [settings, setSettings] = useState(DEFAULT_SITE_SETTINGS);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async (orgId) => {
    try {
      const effectiveOrgId = orgId !== undefined ? orgId : getActiveOrgId();
      const data = await adminApi.getPublicSiteSettings(effectiveOrgId);
      if (data && data.settings) setSettings(data.settings);
    } catch {
      // Silent by design — keep the last known value (or the defaults).
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [refresh]);

  const value = useMemo(() => ({ settings, loading, refresh }), [settings, loading, refresh]);

  return <SiteSettingsContext.Provider value={value}>{children}</SiteSettingsContext.Provider>;
}

/**
 * Bridge component: mounted inside OrgProvider to re-fetch site settings
 * when the active org changes (MTN §4.3). The public settings endpoint
 * accepts an orgId query param and returns the merged org + platform view.
 *
 * Must be placed as a child of BOTH SiteSettingsProvider (for refresh) and
 * OrgProvider (for activeOrg). See App.jsx.
 */
export function SiteSettingsOrgBridge({ orgId }) {
  const { refresh } = useSiteSettings();

  useEffect(() => {
    refresh(orgId || null);
  }, [orgId, refresh]);

  return null;
}

export function useSiteSettings() {
  const ctx = useContext(SiteSettingsContext);
  if (!ctx) throw new Error('useSiteSettings must be used inside <SiteSettingsProvider>');
  return ctx;
}
